import PropTypes from "prop-types";
import { Grid, Typography } from "@mui/material";
import { isEmpty, range, isNil } from "ramda";

import withAlgoliaTracking from "components/withAlgoliaTracking";
import Placeholder from "components/Placeholder";
import StraightUp from "components/StraightUpCards/components/StraightUp";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import { useTrackAccountEvents, useUsers } from "hooks";

import StarringYou from "./components/StarringYou";
import { Root, StyledGrid, ContentScroll, ItemScrolleable, Title } from "./styles";

export const placeholderItemsToRenderSmall = 12;
export const placeholderItemsToRenderBig = 8;

const StarringYouAlgolia = withAlgoliaTracking(StarringYou);
const StraightUpAlgolia = withAlgoliaTracking(StraightUp);

const StarringYouCards = ({
  items,
  loading = false,
  title = "Starring You Ecards",
  size = "big",
  lazy = true,
  overflow = false,
  center = false,
  isGrid = true,
  showDetails = false,
  modulePosition,
}) => {
  const { trackSelectItemEvents } = useTrackAccountEvents();
  const { currentUser } = useUsers();
  const hasModulePosition = Boolean(modulePosition);

  const isSmall = size === "small";
  const lg = isSmall ? 2 : 3;

  const renderTitle = () => {
    if (loading && !isNil(title))
      return (
        <Typography data-testid="starringyoucards-placeholder" variant="h2">
          <Placeholder variant="text" />
        </Typography>
      );
    if (title && !isEmpty(items))
      return (
        <Title variant="h2" isGrid={isGrid}>
          {title}
        </Title>
      );
    return null;
  };

  const renderContentScroll = () => {
    if (loading)
      return range(0, 6).map((index) => (
        <ItemScrolleable key={`StarringYouCards-placeholder-${index}`}>
          <StarringYouAlgolia loading />
        </ItemScrolleable>
      ));

    return (
      items &&
      !isEmpty(items) &&
      items.map((item, index) => (
        <ItemScrolleable key={`starring-you-cards-${size}-${item.id}`}>
          <StarringYouAlgolia lazy={index < 8 ? false : lazy} item={item} overflow={overflow} />
        </ItemScrolleable>
      ))
    );
  };

  const renderContentGrid = () => {
    const count = isSmall ? placeholderItemsToRenderSmall : placeholderItemsToRenderBig;
    if (loading)
      return range(0, count).map((index) => (
        <Grid key={`StarringYouCards-placeholder-${index}`} item xs={6} sm={3} md={3} lg={lg}>
          <StarringYouAlgolia loading />
        </Grid>
      ));

    return (
      items &&
      !isEmpty(items) &&
      items.map((item, index) => {
        const isStraightUp = TemplateGroupPresenter.isStraightUp(item);
        const sharedProps = {
          lazy: index < 8 ? false : lazy,
          item,
          hasModulePosition,
          onClick: hasModulePosition
            ? () =>
                trackSelectItemEvents(currentUser, item, modulePosition ? `${modulePosition}:${index + 1}` : undefined)
            : undefined,
        };

        return (
          <Grid key={`starring-you-cards-${size}-${item.id}`} item xs={6} sm={3} md={3} lg={lg}>
            {isStraightUp ? (
              <StraightUpAlgolia {...sharedProps} />
            ) : (
              <StarringYouAlgolia {...sharedProps} overflow={overflow} showDetails={showDetails} />
            )}
          </Grid>
        );
      })
    );
  };

  return (
    <Root isGrid={isGrid}>
      {renderTitle()}
      {!isGrid ? (
        <ContentScroll>{renderContentScroll()}</ContentScroll>
      ) : (
        <StyledGrid container spacing={2} center={center} data-testid="starringyoucards-container">
          {renderContentGrid()}
        </StyledGrid>
      )}
    </Root>
  );
};

StarringYouCards.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      attributes: PropTypes.shape({}),
    }),
  ),
  loading: PropTypes.bool,
  title: PropTypes.string,
  size: PropTypes.oneOf(["small", "big"]),
  lazy: PropTypes.bool,
  overflow: PropTypes.bool,
  center: PropTypes.bool,
  isGrid: PropTypes.bool,
  showDetails: PropTypes.bool,
  modulePosition: PropTypes.number,
};

export default StarringYouCards;
